// change username, email and password in SettingsPage.js in accountpage
import {
  updateEmail,
  updatePassword,
  signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { doc, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, auth, functions } from '../firebase';
import {
  asyncActionStart,
  asyncActionFinish,
  asyncActionError
} from './asyncActions';
import { openSnackbar } from './snackbarActions';
import { reAuthenticateUser, fetchAuthUserOnce } from './authActions';
import { dispatchFirebaseError } from './errorsActions';
import { openModal, closeModal } from './modalActions';

export const changeEmail = newEmail => async dispatch => {
  dispatch(asyncActionStart());
  dispatch(openModal('CircularLoadingModal'));
  try {
    const user = auth.currentUser;
    const userID = user.uid;
    // change the email in user auth profile
    await updateEmail(user, newEmail);

    // change the email in usersPrivate and usersPublic docs using batch
    const batch = writeBatch(firestore);
    const userRefPrivate = doc(firestore, 'usersPrivate', userID);
    const userRefPublic = doc(firestore, 'usersPublic', userID);
    batch.update(userRefPrivate, { email: newEmail });
    batch.update(userRefPublic, { email: newEmail });
    await batch.commit();

    dispatch(fetchAuthUserOnce()); // get the latest currentUser and providerData
    dispatch(
      openSnackbar('SuccessSnackbar', {
        message: 'successfully changed email!'
      })
    );
    dispatch(asyncActionFinish());
    dispatch(closeModal());
    const success = true;
    return success;
  } catch (error) {
    console.error(error);
    dispatch(asyncActionError());
    // might need to re-authenticate
    if (error.code === 'auth/requires-recent-login') {
      dispatch(reAuthenticateUser(error));
      // if it's not a reauth error but another type of firebase error
    } else {
      dispatch(dispatchFirebaseError(error));
      dispatch(closeModal());
    }
  }
  return null;
};

export const changePassword = (
  newPassword,
  changedOrAdded
) => async dispatch => {
  dispatch(asyncActionStart());
  dispatch(openModal('CircularLoadingModal'));
  try {
    const user = auth.currentUser;
    await updatePassword(user, newPassword);

    dispatch(fetchAuthUserOnce()); // get the latest currentUser and providerData
    let message;
    if (changedOrAdded === 'changed') {
      message = 'Successfully changed password!';
    } else if (changedOrAdded === 'added') {
      message = 'Successfully added password!';
    }
    dispatch(
      openSnackbar('SuccessSnackbar', {
        message
      })
    );
    dispatch(asyncActionFinish());
    dispatch(closeModal());
    const success = true;
    return success;
  } catch (error) {
    console.error(error);
    dispatch(asyncActionError());
    // might need to re-authenticate
    if (error.code === 'auth/requires-recent-login') {
      dispatch(reAuthenticateUser(error));
      // if it's not a reauth error but another type of firebase error
    } else {
      dispatch(dispatchFirebaseError(error));
      dispatch(closeModal());
    }
  }
  return null;
};

// map the auth / functions error codes we expect during deletion to a readable
// snackbar message
const deleteAccountErrorMessage = error => {
  switch (error.code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect password. Please try again.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Re-authentication was cancelled, so your account was not deleted.';
    default:
      return 'Something went wrong and your account was not deleted. Please try again.';
  }
};

// permanently delete the currently signed-in user's account. Deletion is
// irreversible, so we first require a fresh re-authentication (password credential
// for password users, a Google popup for Google users). We then call the
// deleteMyAccount cloud function, which erases the user's data + the copies of
// them that live in other users' records and removes the firebase auth user.
// Finally we sign out locally so the auth listener clears redux and PrivateRoute
// redirects away from the settings page.
export const deleteAccount = password => async dispatch => {
  dispatch(asyncActionStart());
  try {
    const user = auth.currentUser;
    const providerIds = user.providerData.map(provider => provider.providerId);

    if (providerIds.includes('password')) {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
    } else if (providerIds.includes('google.com')) {
      await reauthenticateWithPopup(user, new GoogleAuthProvider());
    }

    const deleteMyAccount = httpsCallable(functions, 'deleteMyAccount');
    await deleteMyAccount();

    await signOut(auth);

    dispatch(closeModal());
    dispatch(asyncActionFinish());
    dispatch(
      openSnackbar('SuccessSnackbar', {
        message: 'Your account has been permanently deleted.'
      })
    );
    return true;
  } catch (error) {
    console.error(error);
    dispatch(asyncActionError());
    // leave the modal open so the user can fix their password / retry
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: deleteAccountErrorMessage(error)
      })
    );
    return false;
  }
};
